import { type FC, type ReactNode, use, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { EnhancedManCode, EnhancedManCodeId } from '#api/enhancements/enhancedTmsApiTypes'
import { Button } from '#components/Button'
import { Icon } from '#components/Icon'
import { Spinner } from '#components/Spinner'
import { TabularInformationOverlay } from '#components/tabularOverlay/TabularInformationOverlay'
import type { ManCodeApiConfiguration } from '#features/Configuration/ApiConfiguration'
import { InlineErrorDisplay } from '#features/ErrorHandling/ErrorDisplay'
import { InlineErrorBoundary } from '#features/ErrorHandling/InlineErrorBoundary'
import { useInlineErrorContext } from '#features/ErrorHandling/InlineErrorContext'
import { loadManCode } from '#features/ManCode/loadManCode'
import { ManCodeJournalTab } from '#features/ManCode/ManCodeJournalTab'
import { useManCodeInformation, useManCodeTitle } from '#features/ManCode/useManCodeInformation'
import DownloadIcon from '#icons/download-16.svg'
import { InternalManCodeOverlayContext } from './InternalManCodeOverlayContext'

type DisplayedManCodeState = {
  manCode?: EnhancedManCodeId
  manCodeTitle?: string
}

export type ContextData = {
  getState: () => DisplayedManCodeState
  setManCode: (manCode: EnhancedManCodeId | undefined, manCodeTitle?: string) => void
  subscribe: (handler: (state: DisplayedManCodeState) => void) => () => void
}

export const ManCodeInformationOverlay: FC<{ manCode: EnhancedManCode; setIsOpen: (isOpen: boolean) => void }> = ({ manCode, setIsOpen }) => {
  const combinedTitle = useManCodeTitle(manCode)
  const infoGroups = useManCodeInformation(manCode)
  const { t } = useTranslation()

  return (
    <TabularInformationOverlay
      infoGroups={infoGroups}
      isOpen={true}
      setIsOpen={setIsOpen}
      title={
        <>
          <span className="primary">{t('man-code.information-overlay.title.base', 'MAN-Code')}</span>
          <span className="secondary">{combinedTitle}</span>
        </>
      }
      tabs={[
        {
          title: t('man-code.information-overlay.tabs.default', 'Current Configuration'),
          id: 'default',
        },
        {
          title: t('man-code.information-overlay.tabs.journal', 'Code Journal'),
          id: 'journal',
          content: <ManCodeJournalTab manCode={manCode} />,
        },
      ]}
      actions={
        <Button
          className="flex"
          size="m"
          kind="secondary"
          onClick={() => {
            const a = document.createElement('a')
            try {
              const jsonData = JSON.stringify(manCode, null, 2)
              const blob = new Blob([jsonData], { type: 'application/json' })
              a.href = URL.createObjectURL(blob)
              a.download = `${manCode.code}.json`
              document.body.appendChild(a)
              a.click()
            } finally {
              document.body.removeChild(a)
              URL.revokeObjectURL(a.href)
            }
          }}
        >
          <span>{t('man-code.information-overlay.download', 'Download JSON')}</span>
          <Icon size="s" className="icon" iconUrl={DownloadIcon} />
        </Button>
      }
    />
  )
}

export const ErrorOverlay: FC<{ error: unknown; setIsOpen: (isOpen: boolean) => void }> = ({ error, setIsOpen }) => {
  const inlineErrorContext = useInlineErrorContext()
  const { t } = useTranslation()
  const displayedError = inlineErrorContext?.error ?? error
  return (
    <TabularInformationOverlay infoGroups={[]} isOpen={true} setIsOpen={setIsOpen} title={t('man-code.information-overlay.error.title', 'Error')}>
      <InlineErrorDisplay
        error={displayedError}
        kind="inline"
        primaryErrorMessage={t('man-code.information-overlay.error.message', 'Could not load MAN-Code information')}
      />
    </TabularInformationOverlay>
  )
}

export const LoadingManCodeInformationOverlay: FC<{ manCode: EnhancedManCodeId; manCodeTitle?: string; setIsOpen: (isOpen: boolean) => void }> = ({
  manCode,
  manCodeTitle,
  setIsOpen,
}) => {
  const { t } = useTranslation()
  const [{ isLoading, message, error }, setStatus] = useState<{ error?: unknown; message?: string; isLoading: boolean }>({ isLoading: true })
  const [manCodeObject, setManCodeObject] = useState<EnhancedManCode | undefined>(undefined)

  useEffect(() => {
    if (!manCode) {
      return
    }

    const abortController = new AbortController()
    const handler = async () => {
      const apiConfiguration: ManCodeApiConfiguration = {
        manCode,
        mode: 'read-only',
      }
      const result = await loadManCode(apiConfiguration, { setStatus, abortController, includeJournal: true })
      setManCodeObject(result)
    }

    void handler()

    return () => {
      abortController.abort()
    }
  }, [manCode])

  const combinedTitle = useMemo(() => {
    return [manCode, manCodeTitle].filter(Boolean).join(' | ')
  }, [manCode, manCodeTitle])

  return isLoading ? (
    <TabularInformationOverlay
      infoGroups={[]}
      isOpen={true}
      setIsOpen={setIsOpen}
      title={
        <>
          <span className="primary">{t('man-code.information-overlay.title.base', 'MAN-Code')}</span>
          <span className="secondary">{combinedTitle}</span>
        </>
      }
    >
      <Spinner subtitle={message ?? t('man-code.information-overlay.message.loading', 'Loading MAN-Code {{manCode}}…', { manCode })} />
    </TabularInformationOverlay>
  ) : error || !manCodeObject ? (
    <ErrorOverlay error={error} setIsOpen={setIsOpen} />
  ) : (
    <InlineErrorBoundary fallback={<ErrorOverlay error={error} setIsOpen={setIsOpen} />}>
      <ManCodeInformationOverlay manCode={manCodeObject} setIsOpen={setIsOpen} />
    </InlineErrorBoundary>
  )
}

const ContextAwareManCodeInformationOverlay: FC = () => {
  const context = use(InternalManCodeOverlayContext)
  if (!context) {
    throw new Error('ManCodeOverlayContext not found')
  }

  const [manCodeState, setManCodeState] = useState<DisplayedManCodeState>(context.getState())

  useEffect(() => {
    return context.subscribe((state) => {
      setManCodeState(state)
    })
  }, [context.subscribe])

  return manCodeState.manCode ? (
    <LoadingManCodeInformationOverlay
      manCode={manCodeState.manCode}
      manCodeTitle={manCodeState.manCodeTitle}
      setIsOpen={() => context.setManCode(undefined)}
    />
  ) : null
}

export const ManCodeOverlayProvider = ({ children }: { children: ReactNode }) => {
  const contextData = useMemo(() => {
    let state: DisplayedManCodeState = {
      manCode: undefined,
    }
    const eventHandlerSet = new Set<(state: DisplayedManCodeState) => void>()
    const notify = () => {
      for (const handler of eventHandlerSet) {
        handler(state)
      }
    }

    return {
      getState: () => state,
      setManCode: (manCode: EnhancedManCodeId | undefined, manCodeTitle?: string) => {
        state = { ...state, manCode, manCodeTitle }
        notify()
      },
      subscribe: (handler: (state: DisplayedManCodeState) => void) => {
        eventHandlerSet.add(handler)
        return () => {
          eventHandlerSet.delete(handler)
        }
      },
    }
  }, [])

  return (
    <InternalManCodeOverlayContext.Provider value={contextData}>
      {children}
      <ContextAwareManCodeInformationOverlay />
    </InternalManCodeOverlayContext.Provider>
  )
}
